import React from "react"
import { Form, Switch, Row, Col, Select } from "antd"
import { useTranslation } from "react-i18next"
import { SemiBoldLabelText } from "@/UI"
import { DriverSchedule } from "@/types/drivers.types"
import { DateTime } from "luxon"
import LuxonDatePicker from "@/modules/shared/LuxonDatePicker"
import { isEmpty } from "lodash"
import { useFlags } from "flagsmith/react"

const daysOfWeek = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

export type DriverScheduleFormProps = {
  schedule?: DriverSchedule
}

const { TimePicker } = LuxonDatePicker

const DriverScheduleForm: React.FC<DriverScheduleFormProps> = ({ schedule }) => {
  const { t } = useTranslation()
  const flags = useFlags(["nash_edg_demo"])
  const incomingWeekNumbers = Array.from({ length: 7 }, (_, i) =>
    DateTime.now().startOf("week").plus({ weeks: i })
  )
  const options = incomingWeekNumbers.map((week) => ({
    value: week?.weekNumber,
    label: `${t("profile.DriverScheduleForm.weekSelectOption", {
      weekNumber: week?.weekNumber,
      start: week.startOf("week")?.toFormat("LLL d"),
      end: week.endOf("week")?.toFormat("LLL d"),
    })}`,
  }))

  return (
    <div className="flex flex-col gap-y-4 mb-4">
      <div className="flex flex-row justify-between gap-x-4">
        <SemiBoldLabelText>{t("profile.DriversSettings.schedule")}</SemiBoldLabelText>
        {flags?.nash_edg_demo?.enabled && (
          <Select
            mode="multiple"
            maxTagCount={1}
            style={{ minWidth: 150 }}
            popupMatchSelectWidth={false}
            placeholder={t("profile.DriversSettings.selectWeeks")}
            options={options}
            allowClear
          />
        )}
      </div>
      {daysOfWeek.map((day) => (
        <Form.Item key={day} className="mb-0">
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Form.Item
                name={["schedule", "weekdays", day, "enabled"]}
                valuePropName="checked"
                noStyle
              >
                <Switch />
              </Form.Item>
              <span className="ml-2 capitalize">{t(`common.${day}`)}</span>
            </Col>
            <Col span={16}>
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name={["schedule", "weekdays", day, "start"]} noStyle>
                    <TimePicker
                      format="HH:mm"
                      placeholder={t("common.start")}
                      className="w-full"
                      disabled={!Form.useWatch(["schedule", "weekdays", day, "enabled"])}
                      disabledTime={() => {
                        return {
                          disabledMinutes: () =>
                            Array.from({ length: 60 }, (_, i) => i).filter(
                              (min) => ![0, 15, 30, 45].includes(min)
                            ),
                        }
                      }}
                      hideDisabledOptions
                      defaultValue={
                        !isEmpty(schedule?.weekdays?.[day]?.start)
                          ? DateTime.fromISO(schedule?.weekdays?.[day]?.start || "")
                          : undefined
                      }
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name={["schedule", "weekdays", day, "end"]} noStyle>
                    <TimePicker
                      format="HH:mm"
                      placeholder={t("common.end")}
                      className="w-full"
                      disabled={!Form.useWatch(["schedule", "weekdays", day, "enabled"])}
                      disabledTime={() => {
                        return {
                          disabledMinutes: () =>
                            Array.from({ length: 60 }, (_, i) => i).filter(
                              (min) => ![0, 15, 30, 45].includes(min)
                            ),
                        }
                      }}
                      hideDisabledOptions
                      defaultValue={
                        !isEmpty(schedule?.weekdays?.[day]?.end)
                          ? DateTime.fromISO(schedule?.weekdays?.[day]?.end || "")
                          : undefined
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form.Item>
      ))}
    </div>
  )
}

export default DriverScheduleForm
